import { Box } from "@mui/material";
import { useDebouncedCallback } from "@react-hookz/web";
import type { EditorState, LexicalEditor, SerializedEditorState } from "lexical";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import type { Json } from "../../../__definitions__/database-definitions";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import type { PreviewProps } from "../../email-template-editor/preview/types";
import { useClaims } from "../../generic/hooks/use-claims";
import { StyledEditorContainer } from "../../lexical-email-moment/email-template-layout";
import type { ToolbarSettings } from "../../lexical-email-moment/hooks/use-email-toolbar-settings";
import { makeEmailToolbarTopLevelElements } from "../../lexical-email-moment/utils";
import { useFilterTags } from "../../merge-tag/components/use-filter-tags";
import { DefaultBodyPlugins } from "../../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../../message/editor/default-content-editable";
import { DefaultPlaceholder } from "../../message/editor/default-placeholder";
import { useLexicalContext } from "../../message/editor/lexical-context";
import { LexicalEditorWrapper } from "../../message/editor/lexical-editor-wrapper";
import { GenericToolbar } from "../../message/editor/plugins/generic-toolbar/generic-toolbar";
import { useMergeTags } from "../../moment/hooks/use-merge-tags";
import type { EmailDesignSchema } from "../types/email-design-type";

export const EmailFooterEditor: FC<PreviewProps> = ({
  data: {
    footer,
    footer_layout_settings,
    layout_settings,
    text_settings,
    button_settings,
    divider_settings,
  },
  badgeProps: { editor },
}) => {
  const methods = useFormContext<EmailDesignSchema & { unrecoverable_error: boolean }>();
  const { setValue, control } = methods;
  const templateId = useWatch({ control, name: "id" });
  const claims = useClaims();

  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editor === "footer") {
      footerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editor]);

  const { data: tags } = useMergeTags();

  const updateDesign = useDebouncedCallback(
    (value: EditorState) => {
      value.read(() => {
        setValue("footer", {
          ...value.toJSON(),
          template: footer?.template,
        });
      });
    },
    [footer],
    500,
  );

  const paperWidth = layout_settings?.max_paper_width
    ? `${layout_settings?.max_paper_width}px`
    : "100%";

  const toolbarSettings = useMemo(
    () =>
      editor === "footer"
        ? {
            topLevelElements: makeEmailToolbarTopLevelElements({
              settings: {
                emailDesigns: [],
                setEmailDesign: () => {},
              },
            }),
            channel: "email" as const,
          }
        : undefined,
    [editor],
  );

  const onError = useSupabaseCallback(
    async ({ supabase }, e: Error, ed: LexicalEditor, undoStack: SerializedEditorState[]) => {
      console.error(e);
      setValue("unrecoverable_error", true);
      toast.error(`Editor Error ${e.message}, please contact support.`);
      await supabase.rpc("log_corrupt_lexical_state", {
        identifier: { type: "email_design", email_design_id: templateId, claims: claims ?? null },
        stack: {
          content: ed.getEditorState().toJSON() as unknown as Json,
          stack: undoStack as unknown as Json,
        },
      });
    },
    [claims, setValue, templateId],
  );

  const disabled = editor !== "footer";

  const filterTags = useFilterTags(tags?.filter((x) => x.version === 2) ?? [], {
    toTarget: "employee",
    target_connection_type: null,
    isManagerConnection: false,
  });

  return (
    footer && (
      <Box ref={footerRef} bgcolor={layout_settings?.background_colour}>
        <LexicalEditorWrapper
          channelName="work_email"
          keyID={templateId}
          onError={(...args) => void onError(...args).catch(console.error)}
          disabled={disabled}
          renderLayout={(editorComponent) => (
            <Box
              sx={{
                p: 0,
                m: 0,
                maxWidth: "100%",
                maxHeight: "80vh",
                overflow: "auto",
                backgroundColor: layout_settings?.background_colour,
              }}
            >
              <StyledEditorContainer
                width={paperWidth}
                settings={{
                  text: text_settings ?? null,
                  layout: layout_settings ?? null,
                  footer_layout: footer_layout_settings ?? null,
                  buttons: button_settings ?? null,
                  dividers: divider_settings ?? null,
                }}
                editorComponent={editorComponent}
                templateSettings={footer?.template}
                footer={true}
              />
            </Box>
          )}
          filterTags={filterTags}
          format="email"
          design={footer ?? undefined}
          slots={{
            contentEditable: ({ focusEditor, onRef }) => (
              <DefaultContentEditable
                focusEditor={focusEditor}
                onRef={onRef}
                channel="work_email"
                disabled={disabled}
                scrollerProps={{ sx: { minHeight: undefined } }}
              />
            ),
            placeholder: (
              <DefaultPlaceholder
                placeholderSettings={{
                  font: text_settings?.font ?? "Arial",
                  fontSize: text_settings?.font_size,
                  offset: footer_layout_settings?.paper_padding_side,
                }}
              />
            ),
            plugins: {
              head: () => <HeadPlugins toolbarSettings={toolbarSettings} disabled={disabled} />,
              body: (historyState) => (
                <DefaultBodyPlugins
                  channel="work_email"
                  format="email"
                  disabled={false}
                  onChange={updateDesign}
                  toolbarSettings={toolbarSettings}
                  styleableColumnConfig={layout_settings}
                  historyState={historyState}
                  v1Tags={tags?.filter((x) => x.version === 1) ?? []}
                />
              ),
            },
          }}
        />
      </Box>
    )
  );
};

const HeadPlugins: FC<{
  toolbarSettings?: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventuall be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  if (!toolbarSettings) {
    return null;
  }

  return (
    <>
      <GenericToolbar
        channel={toolbarSettings.channel}
        topLevelElements={toolbarSettings.topLevelElements}
        disabled={!!disabled}
        openModal={handleOpen}
      />
    </>
  );
};
